import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Camera, Mail, Pencil, Images, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useFAB } from "@/contexts/FABContext";

const MINI_FABS = [
  { label: "Bulk",  icon: <Layers className="h-5 w-5" />, to: "/assets/bulk" },
  { label: "Scene", icon: <Images className="h-5 w-5" />, to: "/assets/new?mode=scene" },
  { label: "Photo", icon: <Camera className="h-5 w-5" />, to: "/assets/new" },
  { label: "Email", icon: <Mail  className="h-5 w-5" />, to: "/settings/forwarding" },
];

const LONG_PRESS_MS = 400;

export default function PrimaryFAB() {
  const { config } = useFAB();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();
  const lastScrollY = useRef(0);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  // Scroll-aware visibility
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      if (y < 10) setVisible(true);
      else if (y > lastScrollY.current + 5) setVisible(false);
      else if (y < lastScrollY.current - 5) setVisible(true);
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const startLongPress = useCallback(() => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setOpen(true);
    }, LONG_PRESS_MS);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTap = useCallback(() => {
    cancelLongPress();
    if (longPressFired.current) return;
    if (open) {
      setOpen(false);
    } else {
      navigate("/assets/new");
    }
  }, [open, navigate, cancelLongPress]);

  // Page-specific override
  if (config) {
    if (config.hidden) return null;
    return (
      <button
        onClick={config.onClick}
        disabled={config.disabled}
        className={cn(
          "fixed bottom-20 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full shadow-md transition-all duration-200 hover:scale-105 active:scale-95 md:bottom-6 min-w-[44px] min-h-[44px]",
          config.variant === "destructive"
            ? "bg-destructive text-destructive-foreground"
            : "bg-primary text-primary-foreground",
          config.disabled && "opacity-50 pointer-events-none",
          visible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
        )}
        aria-label={config.label}
      >
        {config.icon}
      </button>
    );
  }

  // Default: speed dial
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-background/60 backdrop-blur-sm transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mini FABs */}
      {open &&
        MINI_FABS.map((item, i) => (
          <button
            key={item.label}
            onClick={() => {
              setOpen(false);
              navigate(item.to);
            }}
            className={cn(
              "fixed right-6 z-40 flex items-center gap-3 animate-fade-in transition-all duration-200",
              visible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
            )}
            style={{
              bottom: `calc(${typeof window !== "undefined" && window.innerWidth >= 768 ? "1.5rem" : "5rem"} + ${(i + 1) * 4.5}rem)`,
            }}
          >
            <span className="rounded-full bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-md border border-border">
              {item.label}
            </span>
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-secondary-foreground shadow-md">
              {item.icon}
            </span>
          </button>
        ))}

      {/* Main FAB */}
      {/* Invisible enlarged touch target behind the visible FAB */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          startLongPress();
        }}
        onPointerUp={(e) => {
          e.preventDefault();
          cancelLongPress();
          handleTap();
        }}
        onPointerCancel={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onContextMenu={(e) => e.preventDefault()}
        className={cn(
          "fixed bottom-20 right-6 z-40 flex items-center justify-center rounded-full transition-all duration-200 md:bottom-6",
          // Visible circle is 56px; pad with 10px transparent hit area on each side = 76px touch target
          "h-[76px] w-[76px] -m-[10px]",
          visible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
        )}
        style={{ touchAction: "manipulation" }}
        aria-label={open ? "Close menu" : "Add asset"}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform duration-200 hover:scale-105 active:scale-95">
          <Plus
            className={cn(
              "h-6 w-6 transition-transform duration-200",
              open && "rotate-45"
            )}
          />
        </span>
      </button>
    </>
  );
}
